/**
 * Cart Delivery Validator
 *
 * Runs on the cart page and whenever the cart drawer is opened.
 * Reads cart.attributes to validate the stored delivery date against the
 * current Malaysia Time (MYT = UTC+8, no DST) cutoff.
 *
 * Responsibilities:
 *   - Show the delivery banner in "normal" or "stale" state
 *   - Soft-disable the checkout button when the date is stale
 *   - Render the inline date+time picker in the banner
 *   - On confirm: POST /cart/update.js with new date/time and re-render the cart
 *
 * Reads configuration from data attributes on [data-cart-delivery-banner]:
 *   data-cutoff-hour       - Number (0–23), default 18
 *   data-global-closed     - Comma-separated YYYY-MM-DD dates (store-wide closures)
 *
 * The time slots shown in the inline picker come from a global theme setting
 * passed via the banner's data-time-slots attribute, or fall back to defaults.
 */

(function () {
  "use strict";

  // ─────────────────────────────────────────────────────────────────────────
  // MYT Timezone Utilities (mirrors delivery-selector.js)
  // ─────────────────────────────────────────────────────────────────────────

  function getBakeryNow() {
    const utcMs = Date.now() + new Date().getTimezoneOffset() * 60000;
    return new Date(utcMs + 8 * 3600000);
  }

  function isAfterCutoff(cutoffHour) {
    return getBakeryNow().getHours() >= cutoffHour;
  }

  /**
   * Returns the earliest valid delivery date (midnight MYT) for a product
   * with the given minPreorderDays. Always uses 1 day as the minimum.
   */
  function getEffectiveMinDate(cutoffHour, minPreorderDays) {
    const minDays = Math.max(0, minPreorderDays || 1);
    const now = getBakeryNow();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    const effectiveMinDays = minDays + (isAfterCutoff(cutoffHour) ? 1 : 0);
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + effectiveMinDays);
    return minDate;
  }

  function getBlockedDateStrings(globalClosedRaw) {
    if (!globalClosedRaw) return [];
    return globalClosedRaw
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((d) => {
        const u = new Date(d);
        u.setHours(0, 0, 0, 0);
        return u.toDateString();
      });
  }

  /**
   * Returns true if the given ISO date string is still valid as a delivery date
   * using the cart-level minimum (1 day, i.e. we check against the strictest
   * possible product rule so any product in the cart stays valid).
   * The cart-level check uses minPreorderDays=1 since we don't know which product's
   * min is strictest from the cart page. Individual products enforce their own minimum
   * on the product page via prefillFromCart() + CONFLICT mode.
   */
  function isCartDateValid(isoDateStr, cutoffHour, globalClosedRaw) {
    if (!isoDateStr) return false;
    const date = new Date(isoDateStr);
    if (isNaN(date.getTime())) return false;

    const minDate = getEffectiveMinDate(cutoffHour, 1);
    const dateDay = new Date(date);
    dateDay.setHours(0, 0, 0, 0);
    if (dateDay < minDate) return false;

    const blocked = getBlockedDateStrings(globalClosedRaw);
    return !blocked.includes(dateDay.toDateString());
  }

  /**
   * Parse "February 3, 2026" → ISO string at noon (avoids date boundary issues).
   */
  function parseStoredDate(dateStr) {
    if (!dateStr || typeof dateStr !== "string") return null;
    const d = new Date(dateStr.trim());
    if (isNaN(d.getTime())) return null;
    d.setHours(12, 0, 0, 0);
    return d.toISOString();
  }

  function formatDate(isoStr) {
    if (!isoStr) return "";
    return new Date(isoStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Cart Delivery Banner Controller
  // ─────────────────────────────────────────────────────────────────────────

  function CartDeliveryValidator(bannerEl) {
    this.banner = bannerEl;
    this.cutoffHour = parseInt(bannerEl.dataset.cutoffHour) || 18;
    this.globalClosed = bannerEl.dataset.globalClosed || "";

    // Default time slots — fallback when no product constraint data is available.
    // Delivery starts at 1pm; products may still override via available_time_slots.
    // MUST use the canonical "H:MMpm - H:MMpm" spelling: the order tag built
    // from this value is parsed downstream (ordering app, route planner) and
    // shorthand like "1pm-3pm" fails that parse, silently dropping the slot.
    this.defaultTimeSlots = [
      "1:00pm - 3:00pm",
      "3:00pm - 5:00pm",
      "5:00pm - 7:00pm",
    ];

    // Per-cart constraints extracted from line item hidden properties.
    // Populated by _extractCartConstraints() during validate().
    // Used by _generateDates() and _renderTimeSlots() so the cart picker
    // respects every product's min_preorder_days, unavailable_delivery_dates,
    // and available_time_slots — not just the global defaults.
    this.cartMinPreorderDays = 1;
    this.cartBlockedDates = null; // null = falls back to global closed dates only
    this.cartTimeSlots = this.defaultTimeSlots;

    this.selectedNewDate = null;
    this.selectedNewTime = null;
    this.pickerOffset = 0;
    this.datesPerPage = 3;

    this._bindElements();
    this._bindEvents();
  }

  CartDeliveryValidator.prototype._bindElements = function () {
    const b = this.banner;
    this.normalEl = b.querySelector("[data-banner-normal]");
    this.staleEl = b.querySelector("[data-banner-stale]");
    this.pickerEl = b.querySelector("[data-banner-picker]");

    this.dateEl = b.querySelector("[data-banner-date]");
    this.timeEl = b.querySelector("[data-banner-time]");
    this.phoneEl = b.querySelector("[data-banner-phone]");
    this.staleDateEl = b.querySelector("[data-banner-stale-date]");

    this.editBtn = b.querySelector("[data-banner-edit-btn]");
    this.updateBtn = b.querySelector("[data-banner-update-btn]");
    this.pickerClose = b.querySelector("[data-banner-picker-close]");
    this.confirmBtn = b.querySelector("[data-banner-confirm-btn]");
    this.monthLabel = b.querySelector("[data-banner-month-label]");
    this.prevDatesBtn = b.querySelector("[data-banner-prev-dates]");
    this.nextDatesBtn = b.querySelector("[data-banner-next-dates]");
    this.datesWrapper = b.querySelector("[data-banner-dates-wrapper]");
    this.timeSlotsSection = b.querySelector("[data-banner-time-slots]");
    this.timeSlotsGrid = b.querySelector("[data-banner-time-slots-grid]");
    this.pickerError = b.querySelector("[data-banner-picker-error]");
  };

  CartDeliveryValidator.prototype._bindEvents = function () {
    if (this.editBtn) {
      this.editBtn.addEventListener("click", this._openPicker.bind(this));
    }
    if (this.updateBtn) {
      this.updateBtn.addEventListener("click", this._openPicker.bind(this));
    }
    if (this.pickerClose) {
      this.pickerClose.addEventListener("click", this._closePicker.bind(this));
    }
    if (this.confirmBtn) {
      this.confirmBtn.addEventListener("click", this._confirmUpdate.bind(this));
    }
    if (this.prevDatesBtn) {
      this.prevDatesBtn.addEventListener("click", () => {
        if (this.pickerOffset > 0) {
          this.pickerOffset -= this.datesPerPage;
          this._renderPickerDates();
        }
      });
    }
    if (this.nextDatesBtn) {
      this.nextDatesBtn.addEventListener("click", () => {
        this.pickerOffset += this.datesPerPage;
        this._renderPickerDates();
      });
    }
  };

  /**
   * Reads hidden per-product delivery constraints from cart line item properties
   * (written by delivery-selector.js buildLineItemProperties) and computes:
   *
   *   minPreorderDays  — the STRICTEST minimum across all delivery products in the cart
   *   blockedDates     — UNION of per-product unavailable dates + global store closures
   *   timeSlots        — INTERSECTION of time slots available across all delivery products
   *
   * Non-delivery products (bags, candles) have no "_time_slots" property and are skipped.
   */
  CartDeliveryValidator.prototype._extractCartConstraints = function (cartItems) {
    var maxMinPreorderDays = 1;
    var perProductUnavailable = [];
    var timeSlotSets = [];

    cartItems.forEach(function (item) {
      var props = item.properties || {};
      var timeSlotsStr = props["_time_slots"];

      // Skip non-delivery items (bags, candles — they have no _time_slots property)
      if (!timeSlotsStr) return;

      // Strictest min_preorder_days
      var minDays = parseInt(props["_min_preorder_days"], 10);
      if (!isNaN(minDays) && minDays > maxMinPreorderDays) {
        maxMinPreorderDays = minDays;
      }

      // Union of per-product unavailable dates
      var unavailStr = props["_unavailable_dates"];
      if (unavailStr) {
        unavailStr.split(",").forEach(function (d) {
          d = d.trim();
          if (d && !perProductUnavailable.includes(d)) {
            perProductUnavailable.push(d);
          }
        });
      }

      // Collect each product's time slot list for intersection
      var slots = timeSlotsStr.split("|").map(function (s) { return s.trim(); }).filter(Boolean);
      if (slots.length > 0) {
        timeSlotSets.push(slots);
      }
    });

    // Merge global closed dates + per-product unavailable dates into one blocked set
    var globalBlocked = getBlockedDateStrings(this.globalClosed);
    var perProductBlocked = perProductUnavailable.map(function (d) {
      var u = new Date(d);
      u.setHours(0, 0, 0, 0);
      return u.toDateString();
    });
    var blockedDates = globalBlocked.concat(
      perProductBlocked.filter(function (d) { return !globalBlocked.includes(d); })
    );

    // Intersection of time slots — only show slots that work for ALL delivery products
    var timeSlots = this.defaultTimeSlots;
    if (timeSlotSets.length > 0) {
      var intersection = timeSlotSets[0].filter(function (slot) {
        return timeSlotSets.every(function (set) { return set.includes(slot); });
      });
      timeSlots = intersection.length > 0 ? intersection : this.defaultTimeSlots;
    }

    return { minPreorderDays: maxMinPreorderDays, blockedDates: blockedDates, timeSlots: timeSlots };
  };

  /**
   * Validates a date (ISO string) against the cart's ACTUAL constraints:
   *   - cartMinPreorderDays (strictest product in cart)
   *   - cartBlockedDates (union of per-product + global closed dates)
   * Falls back to global-only checks if constraints have not been extracted yet.
   */
  CartDeliveryValidator.prototype._isDateValidForCart = function (isoDateStr) {
    if (!isoDateStr) return false;
    var date = new Date(isoDateStr);
    if (isNaN(date.getTime())) return false;

    var minDate = getEffectiveMinDate(this.cutoffHour, this.cartMinPreorderDays);
    var dateDay = new Date(date);
    dateDay.setHours(0, 0, 0, 0);
    if (dateDay < minDate) return false;

    var blocked = this.cartBlockedDates || getBlockedDateStrings(this.globalClosed);
    return !blocked.includes(dateDay.toDateString());
  };

  CartDeliveryValidator.prototype._openPicker = function () {
    this.selectedNewDate = null;
    this.selectedNewTime = null;
    this.pickerOffset = 0;
    if (this.confirmBtn) this.confirmBtn.disabled = true;
    if (this.timeSlotsSection) this.timeSlotsSection.style.display = "none";
    this._clearPickerError();
    this._renderPickerDates();
    this._showPicker();
  };

  CartDeliveryValidator.prototype._closePicker = function () {
    if (this.pickerEl) this.pickerEl.style.display = "none";
  };

  CartDeliveryValidator.prototype._showPicker = function () {
    if (this.pickerEl) this.pickerEl.style.display = "block";
  };

  CartDeliveryValidator.prototype._showPickerError = function (msg) {
    if (this.pickerError) {
      this.pickerError.textContent = msg;
      this.pickerError.style.display = "block";
    }
  };

  CartDeliveryValidator.prototype._clearPickerError = function () {
    if (this.pickerError) {
      this.pickerError.textContent = "";
      this.pickerError.style.display = "none";
    }
  };

  CartDeliveryValidator.prototype._generateDates = function (offset) {
    var dates = [];
    // Use extracted cart constraints (strictest product rules + global closures)
    var blocked = this.cartBlockedDates || getBlockedDateStrings(this.globalClosed);
    var minDate = getEffectiveMinDate(this.cutoffHour, this.cartMinPreorderDays);
    var today = getBakeryNow();
    today.setHours(0, 0, 0, 0);
    var effectiveMinDays = Math.round((minDate - today) / (1000 * 60 * 60 * 24));

    let added = 0;
    let counter = 0;
    while (added < this.datesPerPage && counter < 90) {
      const date = new Date(today);
      date.setDate(today.getDate() + effectiveMinDays + counter + offset);
      if (!blocked.includes(date.toDateString())) {
        dates.push(date);
        added++;
      }
      counter++;
    }
    return dates;
  };

  CartDeliveryValidator.prototype._renderPickerDates = function () {
    if (!this.datesWrapper) return;
    const dates = this._generateDates(this.pickerOffset);

    if (dates.length === 0) {
      if (this.monthLabel) this.monthLabel.textContent = "";
      this.datesWrapper.innerHTML =
        '<p class="delivery-selector__no-dates">No dates available. Please contact us.</p>';
      if (this.prevDatesBtn) this.prevDatesBtn.disabled = true;
      if (this.nextDatesBtn) this.nextDatesBtn.disabled = true;
      return;
    }

    // Update month label from first visible date
    if (this.monthLabel) {
      this.monthLabel.textContent = dates[0].toLocaleDateString("en-US", { month: "long", year: "numeric" });
    }

    const self = this;
    this.datesWrapper.innerHTML = dates
      .map((date) => {
        const isSelected =
          self.selectedNewDate &&
          new Date(self.selectedNewDate).toDateString() === date.toDateString();
        return `
          <button
            type="button"
            class="delivery-selector__date${isSelected ? " selected" : ""}"
            data-date="${date.toISOString()}"
          >
            <span class="delivery-selector__date-day">${date.getDate()}</span>
            <span class="delivery-selector__date-weekday">${date
              .toLocaleDateString("en-US", { weekday: "short" })
              .toUpperCase()}</span>
          </button>
        `;
      })
      .join("");

    this.datesWrapper.querySelectorAll(".delivery-selector__date").forEach(function (btn) {
      btn.addEventListener("click", function () {
        // Re-validate at click time using full cart constraints
        // (cutoff may have passed, or this date is blocked for a product in the cart)
        var iso = btn.dataset.date;
        if (!self._isDateValidForCart(iso)) {
          self._renderPickerDates();
          self._showPickerError(
            "That date is no longer available. Please select another date."
          );
          return;
        }
        self._clearPickerError();
        self.selectedNewDate = iso;
        self.selectedNewTime = null;
        if (self.confirmBtn) self.confirmBtn.disabled = true;
        self._renderPickerDates(); // re-render to update selected state
        self._renderTimeSlots();
      });
    });

    if (this.prevDatesBtn) this.prevDatesBtn.disabled = this.pickerOffset === 0;
  };

  CartDeliveryValidator.prototype._renderTimeSlots = function () {
    if (!this.timeSlotsGrid || !this.timeSlotsSection) return;
    this.timeSlotsSection.style.display = "block";

    // Use the intersection of time slots across all delivery products in the cart
    var slots = this.cartTimeSlots && this.cartTimeSlots.length > 0
      ? this.cartTimeSlots
      : this.defaultTimeSlots;

    var self = this;
    this.timeSlotsGrid.innerHTML = slots
      .map(function (slot) {
        var isSelected = self.selectedNewTime === slot;
        return (
          '<button type="button" class="delivery-selector__time-slot' +
          (isSelected ? " selected" : "") +
          '" data-time-slot="' + slot + '">' +
          '<span class="delivery-selector__time-slot-text">' + slot + "</span>" +
          "</button>"
        );
      })
      .join("");

    this.timeSlotsGrid.querySelectorAll(".delivery-selector__time-slot").forEach(function (btn) {
      btn.addEventListener("click", function () {
        self.selectedNewTime = btn.dataset.timeSlot;
        self._renderTimeSlots(); // re-render to update selected state
        if (self.confirmBtn) self.confirmBtn.disabled = false;
      });
    });
  };

  CartDeliveryValidator.prototype._confirmUpdate = function () {
    if (!this.selectedNewDate || !this.selectedNewTime) return;

    // Re-validate using full cart constraints before writing to cart.attributes
    if (!this._isDateValidForCart(this.selectedNewDate)) {
      this._showPickerError(
        "That date is no longer available. Please select another date."
      );
      this.selectedNewDate = null;
      this.selectedNewTime = null;
      if (this.confirmBtn) this.confirmBtn.disabled = true;
      this._renderPickerDates();
      if (this.timeSlotsSection) this.timeSlotsSection.style.display = "none";
      return;
    }

    if (this.confirmBtn) {
      this.confirmBtn.disabled = true;
      this.confirmBtn.textContent = "Updating…";
    }

    const newDateFormatted = formatDate(this.selectedNewDate);
    const newTime = this.selectedNewTime;

    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        attributes: {
          "Delivery Date": newDateFormatted,
          "Delivery Time": newTime,
        },
      }),
    })
      .then((r) => r.json())
      .then(() => {
        // Update banner text in-place
        if (this.dateEl) this.dateEl.textContent = newDateFormatted;
        if (this.timeEl) this.timeEl.textContent = newTime;

        // Switch to normal state and re-enable checkout
        this._setNormalState();
        this._closePicker();
        this._enableCheckout();

        if (this.confirmBtn) {
          this.confirmBtn.disabled = false;
          this.confirmBtn.textContent = "Confirm New Date";
        }
      })
      .catch((err) => {
        console.error("Cart Delivery Validator: failed to update cart attributes", err);
        this._showPickerError("Something went wrong. Please try again.");
        if (this.confirmBtn) {
          this.confirmBtn.disabled = false;
          this.confirmBtn.textContent = "Confirm New Date";
        }
      });
  };

  CartDeliveryValidator.prototype._setNormalState = function () {
    if (this.normalEl) this.normalEl.style.display = "";
    if (this.staleEl) this.staleEl.style.display = "none";
    this.banner.classList.remove("cart-delivery-banner--stale");
  };

  CartDeliveryValidator.prototype._setStaleState = function (cartDate) {
    if (this.normalEl) this.normalEl.style.display = "none";
    if (this.staleEl) {
      this.staleEl.style.display = "";
      // Restore the stale copy in case the missing state rewrote it.
      var strong = this.staleEl.querySelector("strong");
      var p = this.staleEl.querySelector("p");
      if (strong) strong.textContent = "Delivery date no longer available";
      if (p) p.style.display = "";
    }
    if (this.staleDateEl) this.staleDateEl.textContent = cartDate;
    this.banner.classList.add("cart-delivery-banner--stale");
  };

  // The Door-to-Door add-on product's variant (RM5 service). Kept here so
  // the banner can offer it to DELIVERY customers and hide it for pickup.
  var D2D_VARIANT_ID = 45969902534716;

  /**
   * Shows the fulfillment-method row (🛵/🏬) and decides whether the
   * door-to-door upsell applies: delivery (or undeclared) carts see the
   * offer, pickup carts never do. Carts already containing the service
   * show the ✓ added state instead of the button.
   */
  CartDeliveryValidator.prototype._renderMethod = function (method, outlet, items) {
    var row = this.banner.querySelector("[data-banner-method-row]");
    var label = this.banner.querySelector("[data-banner-method]");
    var d2d = this.banner.querySelector("[data-banner-d2d]");
    var addBtn = this.banner.querySelector("[data-banner-d2d-add]");
    var addedEl = this.banner.querySelector("[data-banner-d2d-added]");

    var isPickup = /pick/i.test(method);
    var isPostage = /postage/i.test(method);
    var isSameDay = /same-day/i.test(method);
    if (row && label) {
      if (method) {
        row.style.display = "";
        label.textContent = isSameDay
          ? (isPickup ? "⚡ Same-Day Pickup @ " + (outlet || "outlet") : "⚡ Same-Day Delivery")
          : isPostage
          ? "📦 Postage (West Malaysia)"
          : isPickup
            ? "🏬 Self-Pickup @ " + (outlet || "outlet")
            : "🛵 Delivery";
      } else {
        row.style.display = "none";
      }
    }

    if (!d2d) return;
    if (isPickup || isPostage || isSameDay || (items || []).length === 0) {
      d2d.style.display = "none";
      return;
    }
    var hasD2d = (items || []).some(function (it) {
      return it.variant_id === D2D_VARIANT_ID || it.id === D2D_VARIANT_ID;
    });
    d2d.style.display = "";
    // Not added → lobby note + Add button. Added → single confirmation row
    // (the lobby note would contradict it).
    var offerEl = this.banner.querySelector("[data-banner-d2d-offer]");
    if (offerEl) offerEl.style.display = hasD2d ? "none" : "";
    if (addBtn) addBtn.style.display = hasD2d ? "none" : "";
    if (addedEl) addedEl.style.display = hasD2d ? "" : "none";

    if (addBtn && !addBtn._d2dBound) {
      addBtn._d2dBound = true;
      var self = this;
      addBtn.addEventListener("click", function () {
        addBtn.disabled = true;
        addBtn.textContent = "Adding…";
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: D2D_VARIANT_ID, quantity: 1 }),
        })
          .then(function (r) { return r.json(); })
          .then(function () { window.location.reload(); })
          .catch(function () {
            addBtn.disabled = false;
            addBtn.textContent = "Add Door-to-Door Service (RM5)";
          });
      });
    }
  };

  // Cart has items but no delivery date at all — reuse the stale block's
  // UI with "please choose" copy. The <p> (which embeds the stale-date
  // span) is hidden rather than rewritten so the stale state can restore.
  CartDeliveryValidator.prototype._setPostageState = function (batch, conflictNames) {
    this.banner.classList.remove("cart-delivery-banner--stale", "cart-delivery-banner--missing");
    this.banner.classList.add("cart-delivery-banner--postage");
    if (this.dateEl) this.dateEl.textContent = batch;
    if (this.timeEl) this.timeEl.textContent = "Ships within the batch window · arrives 2–3 working days after posting · tracking link sent when posted";
    var dateLabel = this.banner.querySelector("[data-banner-date-label]");
    if (dateLabel) dateLabel.textContent = "Postage batch";
    var picker = this.banner.querySelector("[data-banner-picker]");
    if (picker) picker.style.display = "none";
    var editBtn = this.banner.querySelector("[data-banner-edit-btn]");
    if (editBtn) editBtn.style.display = "none";
    var box = this.banner.querySelector("[data-banner-postage-conflict]");
    if (!box) {
      box = document.createElement("div");
      box.setAttribute("data-banner-postage-conflict", "");
      box.className = "cart-delivery-banner__postage-conflict";
      this.banner.appendChild(box);
    }
    if (conflictNames && conflictNames.length) {
      box.style.display = "";
      var esc = function (s) { return String(s).replace(/</g, "&lt;"); };
      var rows = conflictNames.map(function (c) {
        var name = typeof c === "string" ? c : c.title;
        var key = typeof c === "string" ? "" : c.key;
        return "<li><span>" + esc(name) + "</span>" +
          (key ? " <button type=\"button\" class=\"cart-delivery-banner__remove-btn\" data-banner-remove-line=\"" + esc(key) + "\">Remove</button>" : "") +
          "</li>";
      }).join("");
      box.innerHTML = "<strong>Postage can't be combined with Delivery or Self-Pickup — it's for Egg Yolk Chicken Floss Mochi Crisps only.</strong> " +
        "To check out with postage, remove these items:" +
        "<ul class=\"cart-delivery-banner__remove-list\">" + rows + "</ul>" +
        "<span class=\"cart-delivery-banner__remove-alt\">Or go back to the crisps and choose Delivery / Self-Pickup for the whole order.</span>";
      box.querySelectorAll("[data-banner-remove-line]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          btn.disabled = true; btn.textContent = "Removing…";
          fetch("/cart/change.js", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id: btn.getAttribute("data-banner-remove-line"), quantity: 0 })
          }).then(function () { window.location.reload(); }).catch(function () { window.location.reload(); });
        });
      });
    } else {
      box.style.display = "none";
    }
  };

  // Same-day basket. Lines are checked against their product tags
  // (/products/{handle}.js) — anything not tagged same-day is a pre-order
  // cake that must not share this order. Fee lines (draft-hidden products)
  // and unknown handles are never treated as conflicts.
  CartDeliveryValidator.prototype._handleSameDay = function (cart, items) {
    var self = this;
    var attrs = (cart && cart.attributes) || {};
    var method = attrs["Fulfillment Method"] || "";
    var isPickup = /pickup/i.test(method);
    // Same-day delivery fee per zone (a shipping rate at checkout since 2026-10-05) — mirrors
    // the Same-Day Delivery profile rates and the same-day page's ZONES table.
    var SAME_DAY_ZONE_FEE = { A: 15, B: 18, C: 22, D: 28, E: 45 };
    self._renderMethod(method, attrs["Pickup Location"] || "", items);
    self.banner.style.display = "";
    self.banner.classList.remove("cart-delivery-banner--stale", "cart-delivery-banner--missing");
    self.banner.classList.add("cart-delivery-banner--sameday");
    if (self.normalEl) self.normalEl.style.display = "";
    if (self.staleEl) self.staleEl.style.display = "none";
    if (self.dateEl) self.dateEl.textContent = "Today";
    var chosen = attrs["Delivery Time"] && attrs["Delivery Time"] !== "ASAP" ? attrs["Delivery Time"] : null;
    if (self.timeEl) self.timeEl.textContent = chosen
      ? (isPickup ? "Collect at " + chosen + " today" : "Scheduled for " + chosen + " today")
      : isPickup
      ? "Ready soon — collect any time during opening hours"
      : "As soon as a driver is available" + (attrs["Same-Day Postcode"] ? " · to " + attrs["Same-Day Postcode"] : "")
        + (SAME_DAY_ZONE_FEE[String(attrs["Same-Day Zone"] || "").toUpperCase()] ? " · delivery RM" + SAME_DAY_ZONE_FEE[String(attrs["Same-Day Zone"] || "").toUpperCase()] + " at checkout" : "");
    var picker = self.banner.querySelector("[data-banner-picker]");
    if (picker) picker.style.display = "none";
    var editBtn = self.banner.querySelector("[data-banner-edit-btn]");
    if (editBtn) editBtn.style.display = "none";

    window.__ysSameDayTag = window.__ysSameDayTag || {};
    var cacheKey = "ys_sd_tags";
    try { var stored = JSON.parse(sessionStorage.getItem(cacheKey) || "{}"); Object.keys(stored).forEach(function (k) { if (window.__ysSameDayTag[k] === undefined) window.__ysSameDayTag[k] = stored[k]; }); } catch (e) {}
    var handles = [];
    (items || []).forEach(function (it) { if (it.handle && handles.indexOf(it.handle) < 0) handles.push(it.handle); });
    Promise.all(handles.map(function (h) {
      if (/^same-day-fee/.test(h)) { window.__ysSameDayTag[h] = true; return Promise.resolve(); }
      if (window.__ysSameDayTag[h] !== undefined) return Promise.resolve();
      return fetch("/products/" + h + ".js")
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          var tags = (p && p.tags) || [];
          window.__ysSameDayTag[h] = !p ? true : (tags.indexOf("same-day") >= 0 || tags.indexOf("same-day-fee") >= 0);
        })
        .catch(function () { window.__ysSameDayTag[h] = true; });
    })).then(function () {
      try { sessionStorage.setItem(cacheKey, JSON.stringify(window.__ysSameDayTag)); } catch (e) {}
      var preorder = (items || []).filter(function (it) { return window.__ysSameDayTag[it.handle] === false; });
      var names = preorder.map(function (it) {
        return { title: it.product_title + (it.variant_title && it.variant_title !== "Default Title" ? " — " + it.variant_title : ""), key: it.key };
      });
      self._setSameDayConflict(names);
      if (names.length) {
        self._disableCheckout("Remove pre-order items");
      } else {
        self._enableCheckout();
        self._armSameDayCheckout();
      }
    });
  };

  // Same-day checkout: reserve today's SD number the moment the customer
  // presses Checkout (so the confirmation email shows "SD-07"), then let the
  // normal checkout submit through.
  //
  // ONE delegated listener on document (capture phase), installed when this
  // script loads. The earlier version attached a listener to each Checkout
  // button only after validate() had finished its fetches — and the drawer
  // replaces its Checkout button on every cart change — so for about a second
  // after each add the visible button had no listener and a quick tap skipped
  // the reservation (order #817497, 2026-09-17: the confirmation email showed
  // the Shopify number instead of SD-01). Whether the cart is same-day is now
  // answered synchronously by the Liquid-rendered banner flag
  // (data-ys-same-day, snippets/cart-delivery-banner.liquid) or the last
  // validate() result, then confirmed against /cart.js before a number is
  // spent, so a pre-order cart is never touched and never gets an SD number.
  var SAME_DAY_RESERVE_URL = "https://track.yoursnackiesdesserts.com/api/same-day/reserve";
  var CHECKOUT_BUTTONS = '[name="checkout"], #checkout, .cart__checkout-button';

  function looksSameDayCart() {
    if (window.__ysSameDayCart === true) return true;
    return !!document.querySelector('[data-cart-delivery-banner][data-ys-same-day="1"]');
  }

  function reserveSameDayThenCheckout(btn) {
    if (btn.dataset.sdBusy === "1") return; // double tap — one reservation only
    btn.dataset.sdBusy = "1";
    var was = btn.textContent;
    btn.textContent = "One moment…";
    var go = function () {
      btn.dataset.sdReady = "1";
      delete btn.dataset.sdBusy;
      btn.textContent = was;
      if (btn.form && typeof btn.form.requestSubmit === "function") btn.form.requestSubmit(btn);
      else if (btn.form) btn.form.submit();
      else window.location.href = "/checkout";
    };
    var reserve = function () {
      return fetch(SAME_DAY_RESERVE_URL, { method: "POST" })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { return j && j.sdNo ? { sdNo: j.sdNo, trackUrl: j.trackUrl || "" } : null; })
        .catch(function () { return null; });
    };
    fetch("/cart.js")
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var method = (cart && cart.attributes && cart.attributes["Fulfillment Method"]) || "";
        if (!/same-day/i.test(method)) { window.__ysSameDayCart = false; return null; } // stale flag — not a same-day cart
        return reserve()
          .then(function (r) {
            if (r) return r;
            return new Promise(function (res) { setTimeout(res, 700); }).then(reserve); // one retry
          })
          .then(function (r) {
            if (!r) return null; // app unreachable: the sale matters more — later emails still show SD-NN from the order itself
            // "Same-Day Track" = the tracker link the confirmation email prints (Sam 2026-10-08).
            var attrs = { "Same-Day No": r.sdNo };
            if (r.trackUrl) attrs["Same-Day Track"] = r.trackUrl;
            // Self-pickup has no shipping address, so checkout never asks for a
            // phone; the same-day page collects it, and this is the safety net
            // for a cart that was built before that field existed.
            if (/pickup/i.test(method) && String(cart.attributes["Customer Phone"] || "").replace(/\D/g, "").length < 9) {
              var ph = window.prompt("Your phone number, so the outlet can reach you about your pickup:");
              if (ph && ph.replace(/\D/g, "").length >= 9) attrs["Customer Phone"] = ph.replace(/[^\d+]/g, "");
            }
            return fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attributes: attrs }) });
          });
      })
      .catch(function () {})
      .then(go);
  }

  document.addEventListener("click", function (e) {
    var t = e.target;
    var btn = t && t.closest ? t.closest(CHECKOUT_BUTTONS) : null;
    if (!btn || (btn.tagName !== "BUTTON" && btn.tagName !== "INPUT")) return;
    if (btn.disabled || btn.hasAttribute("data-delivery-blocked")) return; // a guard is showing its own message
    if (btn.dataset.sdReady === "1") return; // number reserved — let it submit
    if (!looksSameDayCart()) return; // pre-order carts: untouched
    e.preventDefault(); e.stopPropagation(); e.stopImmediatePropagation();
    reserveSameDayThenCheckout(btn);
  }, true);

  // Express buttons (Shop Pay etc.) skip the reservation, so they stay hidden
  // for same-day carts (the banner snippet also hides them with CSS).
  CartDeliveryValidator.prototype._armSameDayCheckout = function () {
    window.__ysSameDayCart = true;
    document.querySelectorAll(".additional-checkout-buttons, .cart__dynamic-checkout-buttons").forEach(function (el) { el.style.display = "none"; });
  };

  CartDeliveryValidator.prototype._setSameDayConflict = function (conflictNames) {
    var box = this.banner.querySelector("[data-banner-postage-conflict]");
    if (!box) {
      box = document.createElement("div");
      box.setAttribute("data-banner-postage-conflict", "");
      box.className = "cart-delivery-banner__postage-conflict";
      this.banner.appendChild(box);
    }
    if (!conflictNames || !conflictNames.length) {
      box.style.display = "none";
      this.banner.classList.remove("cart-delivery-banner--postage");
      return;
    }
    // --postage class only so the blocked checkout button scrolls to this box.
    this.banner.classList.add("cart-delivery-banner--postage");
    box.style.display = "";
    var esc = function (s) { return String(s).replace(/</g, "&lt;"); };
    var rows = conflictNames.map(function (c) {
      return "<li><span>" + esc(c.title) + "</span> <button type=\"button\" class=\"cart-delivery-banner__remove-btn\" data-banner-remove-line=\"" + esc(c.key) + "\">Remove</button></li>";
    }).join("");
    box.innerHTML = "<strong>Same-day treats need their own order — pre-order cakes are made to a different schedule.</strong> " +
      "To check out your same-day order, remove these items:" +
      "<ul class=\"cart-delivery-banner__remove-list\">" + rows + "</ul>" +
      "<span class=\"cart-delivery-banner__remove-alt\">Or <button type=\"button\" class=\"cart-delivery-banner__remove-btn\" data-banner-clear-sameday>clear the basket</button> and start a fresh pre-order.</span>";
    box.querySelectorAll("[data-banner-remove-line]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        btn.disabled = true; btn.textContent = "Removing…";
        fetch("/cart/change.js", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: btn.getAttribute("data-banner-remove-line"), quantity: 0 })
        }).then(function () { window.location.reload(); }).catch(function () { window.location.reload(); });
      });
    });
    var clearBtn = box.querySelector("[data-banner-clear-sameday]");
    if (clearBtn) clearBtn.addEventListener("click", function () {
      clearBtn.disabled = true; clearBtn.textContent = "Clearing…";
      fetch("/cart/clear.js", { method: "POST" })
        .then(function () { return fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: { "Fulfillment Method": "", "Delivery Time": "", "Pickup Location": "", "Same-Day Postcode": "", "Same-Day Zone": "", "Same-Day No": "", "Same-Day At": "", "Same-Day Track": "", "Customer Phone": "", "Message on Card": "", "Make it Circle": "" } }) }); })
        .then(function () { window.location.reload(); }).catch(function () { window.location.reload(); });
    });
  };

  CartDeliveryValidator.prototype._setMissingState = function () {
    if (this.normalEl) this.normalEl.style.display = "none";
    if (this.staleEl) {
      this.staleEl.style.display = "";
      var strong = this.staleEl.querySelector("strong");
      var p = this.staleEl.querySelector("p");
      if (strong) strong.textContent = "Please choose your delivery date & time before checking out.";
      if (p) p.style.display = "none";
    }
    this.banner.classList.add("cart-delivery-banner--stale");
  };

  CartDeliveryValidator.prototype._disableCheckout = function (label) {
    var self = this;
    var buttonLabel = label || "Update Delivery Date";

    // Express checkout buttons (Shop Pay / GPay / PayPal) skip the cart button
    // entirely — hide them while checkout is blocked.
    document.querySelectorAll(".additional-checkout-buttons, .cart__dynamic-checkout-buttons").forEach(function (el) {
      el.setAttribute("data-delivery-blocked-express", "true");
      el.style.display = "none";
    });

    // Target only actual button/submit elements, not wrapper divs.
    // [name="checkout"] catches both the cart drawer and cart page submit buttons.
    // #checkout catches the cart page button by ID.
    // .cart__checkout-button catches any theme-specific button class.
    document
      .querySelectorAll('[name="checkout"], #checkout, .cart__checkout-button')
      .forEach(function (btn) {
        if (btn.tagName !== "BUTTON" && btn.tagName !== "INPUT") return;

        // Store original label
        if (!btn.dataset.originalText) {
          btn.dataset.originalText = btn.textContent.trim();
        }

        btn.setAttribute("data-delivery-blocked", "true");
        btn.disabled = false; // keep enabled so the click fires
        btn.title = "";
        btn.textContent = buttonLabel;

        // Remove any previous handler before attaching a fresh one
        if (btn._deliveryClickHandler) {
          btn.removeEventListener("click", btn._deliveryClickHandler);
        }
        btn._deliveryClickHandler = function (e) {
          e.preventDefault();
          e.stopPropagation();
          // Postage conflict: point at the notice instead of the date picker.
          if (self.banner.classList.contains("cart-delivery-banner--postage")) {
            var box = self.banner.querySelector("[data-banner-postage-conflict]");
            if (box) { box.scrollIntoView({ behavior: "smooth", block: "center" }); box.classList.add("is-flash"); setTimeout(function () { box.classList.remove("is-flash"); }, 900); }
            return;
          }
          self._openPicker();

          // Scroll within the cart drawer's own scroll container, not the page
          requestAnimationFrame(function () {
            var target = self.pickerEl || self.banner;
            if (!target) return;
            var scrollContainer =
              target.closest(".scroll-area") ||
              target.closest("[class*='scroll']") ||
              target.closest("cart-drawer");
            if (scrollContainer) {
              var targetTop = target.getBoundingClientRect().top -
                scrollContainer.getBoundingClientRect().top +
                scrollContainer.scrollTop - 12;
              scrollContainer.scrollTo({ top: targetTop, behavior: "smooth" });
            }
          });
        };
        btn.addEventListener("click", btn._deliveryClickHandler);
      });
  };

  CartDeliveryValidator.prototype._enableCheckout = function () {
    document.querySelectorAll("[data-delivery-blocked-express]").forEach(function (el) {
      el.style.display = "";
      el.removeAttribute("data-delivery-blocked-express");
    });
    document
      .querySelectorAll("[data-delivery-blocked]")
      .forEach(function (btn) {
        btn.disabled = false;
        btn.removeAttribute("data-delivery-blocked");
        btn.title = "";

        // Restore original label
        if (btn.dataset.originalText) {
          btn.textContent = btn.dataset.originalText;
          delete btn.dataset.originalText;
        }

        // Remove the delivery click handler
        if (btn._deliveryClickHandler) {
          btn.removeEventListener("click", btn._deliveryClickHandler);
          delete btn._deliveryClickHandler;
        }
      });
  };

  /**
   * Main validation entry point.
   * Fetches /cart.js, checks cart.attributes['Delivery Date'] against current
   * MYT cutoff, and sets the banner to the appropriate state.
   */
  CartDeliveryValidator.prototype.validate = function () {
    var self = this;

    fetch("/cart.js")
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var cartDate = cart && cart.attributes && cart.attributes["Delivery Date"];
        var cartTime = cart && cart.attributes && cart.attributes["Delivery Time"];
        var cartPhone = cart && cart.attributes && cart.attributes["Phone Number"];
        var cartItems = (cart && cart.items) || [];
        var method = (cart && cart.attributes && cart.attributes["Fulfillment Method"]) || "";
        var outlet = (cart && cart.attributes && cart.attributes["Pickup Outlet"]) || "";
        window.__ysSameDayCart = cartItems.length > 0 && /same-day/i.test(method); // read by the Checkout click listener
        self._renderMethod(method, outlet, cartItems);

        if (cartItems.length === 0) {
          // Empty cart — hide banner entirely
          self.banner.style.display = "none";
          self._enableCheckout();
          return;
        }

        // Same-day basket (from /pages/same-day-delivery): no date slot by
        // design — never force the pre-order date picker, and never let a
        // pre-order cake ride along (different schedule, fee and kitchen).
        if (/same-day/i.test(method)) {
          self._handleSameDay(cart, cartItems);
          return;
        }

        var postageBatch = (cart && cart.attributes && cart.attributes["Postage Batch"]) || "";
        if (/postage/i.test(method)) {
          var hasPostable = cartItems.some(function (it) {
            return /egg yolk chicken floss mochi cri/i.test(it.product_title || "");
          });
          if (!hasPostable) {
            // The crisps were removed but the cart is still flagged Postage.
            // Reset to a fresh delivery order: clear the postage attributes and
            // ask for a delivery date/method like any normal cart. Guard against
            // loops with a one-shot flag.
            if (!self._postageResetting) {
              self._postageResetting = true;
              fetch("/cart/update.js", {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ attributes: {
                  "Fulfillment Method": "", "Postage Batch": "", "Delivery Date": "", "Delivery Time": "", "Delivery Type": "", "Pickup Outlet": ""
                } })
              }).then(function () { self._postageResetting = false; self.validate(); })
                .catch(function () { self._postageResetting = false; });
            }
            method = ""; cartDate = ""; cartTime = "";
            self.banner.classList.remove("cart-delivery-banner--postage");
            var oldBox = self.banner.querySelector("[data-banner-postage-conflict]");
            if (oldBox) oldBox.style.display = "none";
            var dl = self.banner.querySelector("[data-banner-date-label]");
            if (dl) dl.textContent = "Delivery date";
            var eb = self.banner.querySelector("[data-banner-edit-btn]");
            if (eb) eb.style.display = "";
            self._renderMethod("", "", cartItems);
            // fall through to the "no date" flow below
          }
        }
        if (/postage/i.test(method) && postageBatch) {
          // Postage order: no delivery date by design. Show batch + rules,
          // and only allow checkout when EVERY item is a postable product
          // (crisps). Shopify's shipping profile is the hard guard; this
          // explains it before the customer hits a dead-end at checkout.
          self.banner.style.display = "";
          var nonPost = cartItems.filter(function (it) {
            return !/egg yolk chicken floss mochi cri/i.test(it.product_title || "");
          });
          var names = nonPost.map(function (it) {
            return { title: it.product_title + (it.variant_title && it.variant_title !== "Default Title" ? " — " + it.variant_title : ""), key: it.key };
          });
          self._setPostageState(postageBatch, names);
          if (nonPost.length) {
            self._disableCheckout("Remove non-postage items");
          } else {
            self._enableCheckout();
          }
          return;
        }

        if (!cartDate) {
          // Cart has items but NO delivery date — e.g. the item was added
          // from a collection-card quick-add that skips the product page
          // selector. This must NEVER reach checkout (orders born without
          // a date are invisible to the kitchen's planning): show the
          // banner in "choose a date" state and force the picker flow.
          var missingConstraints = self._extractCartConstraints(cartItems);
          self.cartMinPreorderDays = missingConstraints.minPreorderDays;
          self.cartBlockedDates    = missingConstraints.blockedDates;
          self.cartTimeSlots       = missingConstraints.timeSlots;
          self.banner.style.display = "";
          self._setMissingState();
          self._disableCheckout("Choose Delivery Date");
          return;
        }

        // Extract per-product delivery constraints from line item hidden properties.
        // This gives us: strictest min_preorder_days, union of unavailable dates,
        // intersection of available time slots — across all delivery products in the cart.
        var constraints = self._extractCartConstraints(cartItems);
        self.cartMinPreorderDays = constraints.minPreorderDays;
        self.cartBlockedDates    = constraints.blockedDates;
        self.cartTimeSlots       = constraints.timeSlots;

        // Show the banner since there is a delivery date
        self.banner.style.display = "";

        // Update displayed values from live cart state
        if (self.dateEl) self.dateEl.textContent = cartDate;
        if (self.timeEl) self.timeEl.textContent = cartTime || "";
        if (self.phoneEl) {
          self.phoneEl.textContent = cartPhone || "";
          var phoneRow = self.phoneEl.closest(".cart-delivery-banner__detail");
          if (phoneRow) phoneRow.style.display = cartPhone ? "" : "none";
        }

        // Validate against ACTUAL cart constraints (not just global 1-day minimum)
        var parsed = parseStoredDate(cartDate);
        if (parsed && self._isDateValidForCart(parsed)) {
          self._setNormalState();
          self._enableCheckout();
        } else {
          self._setStaleState(cartDate);
          self._disableCheckout();
        }
      })
      .catch(function (err) {
        console.error("Cart Delivery Validator: failed to fetch cart", err);
      });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Initialisation
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Initialize a single banner element: create a CartDeliveryValidator, bind events,
   * run initial validation, and store the instance on the element.
   * Safe to call multiple times — skips elements that are already initialized.
   */
  function initBanner(el) {
    if (el._deliveryValidator) return; // already initialized, skip
    const validator = new CartDeliveryValidator(el);
    el._deliveryValidator = validator;
    validator.validate();
  }

  function initBanners() {
    document.querySelectorAll("[data-cart-delivery-banner]").forEach(initBanner);
  }

  /**
   * Re-run validation on all banners.
   *
   * Called after the cart drawer re-renders its contents. Because renderContents()
   * in cart-drawer.js replaces the innerHTML of #CartDrawer, the banner element
   * itself gets replaced with fresh HTML that has no event listeners. We must
   * re-initialize any banner that is new (no _deliveryValidator) AND re-validate
   * any that already existed.
   */
  function revalidateAll() {
    document.querySelectorAll("[data-cart-delivery-banner]").forEach(function (el) {
      if (el._deliveryValidator) {
        // Existing instance — just re-validate (element was not replaced)
        el._deliveryValidator.validate();
      } else {
        // New element after re-render (#CartDrawer innerHTML was replaced).
        // Re-bind all events and validate.
        initBanner(el);
      }
    });
  }

  // Expose initBanner so other scripts (e.g. delivery-selector.js) can trigger
  // re-initialisation on freshly rendered banner elements without importing this file.
  window._initCartDeliveryBanner = initBanner;

  // Run on initial page load
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initBanners);
  } else {
    initBanners();
  }

  // Re-run when the cart drawer opens (Shopify theme dispatches this event)
  document.addEventListener("cart:open", revalidateAll);

  // Re-run when Shopify's PubSub cartUpdate fires (after any add/remove/update)
  // This uses the same event name as the theme's cart.js
  document.addEventListener("cart:refresh", revalidateAll);

  // Re-run whenever the cart drawer re-renders its contents.
  //
  // cart-drawer.js's renderContents() replaces the innerHTML of #CartDrawer entirely.
  // This destroys the banner element and creates a fresh one with no event listeners.
  // The MutationObserver detects this and calls revalidateAll(), which re-initializes
  // any banner that lacks a _deliveryValidator (i.e. freshly rendered ones).
  //
  // We observe #CartDrawer specifically (not the outer cart-drawer element) to catch
  // the exact innerHTML replacement. We debounce with a short timeout to let the DOM
  // fully settle before querying for the new banner element.
  let revalidateTimer = null;

  const cartObserver = new MutationObserver(function (mutations) {
    // Only react to childList mutations (innerHTML replacements), not attribute changes
    const hasChildListChange = mutations.some(function (m) {
      return m.type === "childList";
    });
    if (!hasChildListChange) return;

    // Debounce: cancel any pending revalidation and schedule a fresh one
    clearTimeout(revalidateTimer);
    revalidateTimer = setTimeout(revalidateAll, 50);
  });

  // Observe the inner cart drawer content div (replaced by renderContents)
  const cartDrawerInner = document.querySelector("#CartDrawer");
  if (cartDrawerInner) {
    cartObserver.observe(cartDrawerInner, { childList: true, subtree: false });
  }

  // Fallback: also observe the outer cart-drawer element in case the theme
  // replaces it at a higher level
  const cartDrawer = document.querySelector("cart-drawer");
  if (cartDrawer && !cartDrawerInner) {
    cartObserver.observe(cartDrawer, { childList: true, subtree: true });
  }

  const cartNotification = document.querySelector("cart-notification");
  if (cartNotification) {
    cartObserver.observe(cartNotification, { childList: true, subtree: true });
  }
})();
